import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '@/hooks/usePageSEO';
import { PUBLIC_PAGES } from '@/lib/seo/public-routes';
import { pageMetadata } from '@/lib/seo/metadata';
import { PublicContainer } from '@/components/layouts/PublicContainer';

const LAST_UPDATED = 'July 7, 2026';

const RefundPolicy: React.FC = () => {
  usePageSEO(pageMetadata(PUBLIC_PAGES.legalRefund));
  return (
    <div className="bg-background py-24 sm:py-32">
      <PublicContainer>
        <div className="mx-auto max-w-3xl">
          <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Refund Policy</h1>
          <p className="mt-2 text-base text-muted-foreground">Last updated: {LAST_UPDATED}</p>

          <div className="mt-10 space-y-8 text-base leading-7 text-muted-foreground">

            <p>
              DebtDrone is committed to your satisfaction. This Refund Policy describes when and how you may request
              a refund for any paid subscription purchased through our platform, which uses{' '}
              <strong className="text-foreground">Paddle</strong> as the merchant of record and payment processor.
            </p>

            <section>
              <h2 className="text-xl font-bold tracking-tight text-foreground mt-10 mb-3">1. 30-Day Money-Back Guarantee</h2>
              <p>
                If you are not satisfied with your DebtDrone subscription, you may request a full refund within{' '}
                <strong className="text-foreground">30 days</strong> of your initial purchase or renewal date. No
                questions asked.
              </p>
              <p className="mt-3">
                This guarantee applies to all paid plans (Pro and Enterprise). The Starter (free) plan is not
                eligible as no charge is made.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold tracking-tight text-foreground mt-10 mb-3">2. Eligibility</h2>
              <p>A refund request is eligible when <strong className="text-foreground">all</strong> of the following conditions are met:</p>
              <ul className="list-disc pl-5 mt-3 space-y-2">
                <li>The request is submitted within 30 days of the charge date.</li>
                <li>The subscription was purchased directly through DebtDrone at debtdrone.net.</li>
                <li>The account has not previously received a refund for the same plan within the past 12 months.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold tracking-tight text-foreground mt-10 mb-3">3. Annual Plans</h2>
              <p>
                For annual subscriptions, you may request a full refund within the first 30 days. After the 30-day
                window, refunds are issued on a <strong className="text-foreground">pro-rata basis</strong> for the
                remaining unused full months, at our discretion.
              </p>
              <p className="mt-3">
                Example: if you purchased an annual plan and request a refund after 3 months (outside the 30-day
                window), we will consider a refund for the remaining 9 months of unused service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold tracking-tight text-foreground mt-10 mb-3">4. Non-Refundable Items</h2>
              <p>The following are not eligible for refunds:</p>
              <ul className="list-disc pl-5 mt-3 space-y-2">
                <li>Charges older than 30 days (unless covered by the pro-rata clause above).</li>
                <li>Add-on services or one-time purchases agreed separately under an Enterprise contract.</li>
                <li>Accounts suspended or terminated due to violations of our{' '}
                  <Link to="/legal/terms" className="text-foreground underline underline-offset-2 hover:opacity-70 transition-opacity">
                    Terms of Service
                  </Link>.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold tracking-tight text-foreground mt-10 mb-3">5. How to Request a Refund</h2>
              <p>To request a refund, contact us at:</p>
              <div className="mt-4 rounded-xl border border-border bg-card p-5 space-y-2">
                <p>
                  <strong className="text-foreground">Email:</strong>{' '}
                  <a href="mailto:support@debtdrone.net" className="text-foreground underline underline-offset-2 hover:opacity-70 transition-opacity">
                    support@debtdrone.net
                  </a>
                </p>
                <p>
                  <strong className="text-foreground">Subject line:</strong>{' '}
                  <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded">Refund Request — [your registered email]</span>
                </p>
                <p className="text-sm text-muted-foreground pt-1">
                  Please include your registered email address and the date of the charge. We aim to process
                  all requests within <strong className="text-foreground">3 business days</strong>.
                </p>
              </div>
              <p className="mt-4">
                Refunds are processed by <strong className="text-foreground">Paddle</strong> and will be returned to
                the original payment method. Processing time depends on your bank or card issuer, typically
                5–10 business days.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold tracking-tight text-foreground mt-10 mb-3">6. Account After Refund</h2>
              <p>
                Upon issuing a refund, your account will be downgraded to the free Starter plan at the end of
                the current billing period. You will retain access to your data and scan history; only paid
                features will become unavailable.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold tracking-tight text-foreground mt-10 mb-3">7. Paddle as Merchant of Record</h2>
              <p>
                DebtDrone is operated by an independent solo founder. All DebtDrone subscriptions are sold by{' '}
                <strong className="text-foreground">Paddle.com Market Limited</strong>,
                which acts as the merchant of record. Paddle handles all payment processing, tax collection, and
                invoicing. For payment disputes or chargebacks, Paddle may be involved in the resolution process.
              </p>
              <p className="mt-3">
                For more information about how Paddle handles data, see{' '}
                <a
                  href="https://www.paddle.com/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground underline underline-offset-2 hover:opacity-70 transition-opacity"
                >
                  Paddle's Privacy Policy
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold tracking-tight text-foreground mt-10 mb-3">8. Changes to This Policy</h2>
              <p>
                We reserve the right to modify this Refund Policy at any time. Changes will be posted on this page
                with an updated date. Continued use of the service after changes constitutes acceptance of the
                revised policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold tracking-tight text-foreground mt-10 mb-3">9. Contact</h2>
              <p>
                Questions about this policy? Reach us at{' '}
                <a href="mailto:support@debtdrone.net" className="text-foreground underline underline-offset-2 hover:opacity-70 transition-opacity">
                  support@debtdrone.net
                </a>{' '}
                or visit our{' '}
                <Link to="/docs" className="text-foreground underline underline-offset-2 hover:opacity-70 transition-opacity">
                  Help Center
                </Link>.
              </p>
            </section>

          </div>
        </div>
      </PublicContainer>
    </div>
  );
};

export default RefundPolicy;
